import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { SessionTokenService } from '../services/utils/session-token.service';

/**
 * Marque une requête qui ne doit **pas** porter le jeton Keycloak.
 *
 * Le cas réel est le dépôt présigné : l'URL part vers le stockage d'objets, pas
 * vers l'API. S3 refuse une requête qui présente à la fois une signature en
 * query string et un en-tête `Authorization` (« only one auth mechanism
 * allowed »), et l'en-tête déclencherait en plus un prévol CORS que le bucket
 * n'autorise pas. Le jeton n'y aurait de toute façon aucun sens : c'est la
 * signature qui fait autorité.
 */
export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

/**
 * Cette requête va-t-elle à notre API ?
 *
 * <p><b>Pourquoi une liste blanche.</b> Le jeton partait sur <i>toutes</i> les
 * requêtes, et ne s'omettait que sur demande expresse ({@link SKIP_AUTH}).
 * Une porte ouverte par défaut qu'il fallait penser à refermer : le premier
 * appel vers un service tiers — une police de caractères, une carte, un
 * traceur, un dépôt d'objet — aurait emporté le jeton d'accès de l'élève dans
 * son en-tête <c>Authorization</c>, sans que rien ne le signale. Le piège a
 * déjà mordu une fois, avec le stockage d'objets.</p>
 *
 * <p>Désormais l'inverse : le jeton ne part que vers l'adresse de l'API,
 * telle que l'environnement la déclare. Tout le reste — les traductions, les
 * images, un dépôt présigné, un service qui n'existe pas encore — sort sans
 * en-tête, sans avoir à y penser.</p>
 *
 * <p>Les URL relatives ne visent que cette application : l'API est absolue
 * dans l'environnement, et les fichiers d'<c>assets/</c> n'ont pas à porter
 * d'identité.</p>
 */
export function versNotreApi(url: string): boolean {
  return url.startsWith(environment.BASE_URL);
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  // {@link SKIP_AUTH} reste, et se lit en premier : une requête vers l'API
  // peut avoir ses raisons de partir nue.
  if (req.context.get(SKIP_AUTH) || !versNotreApi(req.url)) {
    return next(req);
  }

  // Le jeton est renouvelé avant de partir s'il allait expirer : c'est ce qui
  // fait prendre effet à un changement de droits sans reconnexion — voir
  // {@link SessionTokenService}.
  const session = inject(SessionTokenService);

  return from(session.current()).pipe(
    // Posé avant `switchMap` : il ne voit que l'échec du renouvellement, pas
    // les erreurs de la requête elle-même. La session est close et la
    // reconnexion déjà lancée ; la requête n'est pas émise, et son appelant
    // reçoit le 401 qu'il aurait reçu du serveur.
    catchError(() =>
      throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Session expirée', url: req.url })),
    ),
    switchMap((token) => {
      if (!token) {
        return next(req); // no token, continue
      }

      const authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });

      return next(authReq);
    })
  );
};
